/**
 * Date-range presets for the Activity Leaderboard (#514).
 *
 * The backend's `/admin/stats/activity-leaderboard` range mode is
 * timezone-agnostic: it takes two UTC ISO instants and does a `createdAt`
 * BETWEEN query. This module converts the admin's LOCAL calendar day / week /
 * month into those instants, so "Today" means the admin's local day (SA time
 * for the broker team) — not UTC. Pure + `now`-injectable so it unit-tests
 * without a clock.
 */

export type RangePreset = 'today' | 'yesterday' | 'last7' | 'thisMonth' | 'custom';

export interface IsoRange {
  /** Inclusive lower bound, UTC ISO. */
  from: string;
  /** Inclusive upper bound, UTC ISO. */
  to: string;
}

export const RANGE_PRESETS: { value: RangePreset; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last7', label: 'Last 7 days' },
  { value: 'thisMonth', label: 'This month' },
  { value: 'custom', label: 'Custom range' },
];

const startOfDay = (d: Date): Date =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
const endOfDay = (d: Date): Date =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);

/** Parse a `YYYY-MM-DD` (native date-input value) as a LOCAL calendar day; null if malformed/overflowing. */
export function parseLocalYmd(ymd: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  const d = new Date(year, month - 1, day);
  // Reject overflow (e.g. 2026-02-31 rolling into March).
  if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) {
    return null;
  }
  return d;
}

/**
 * Resolve the chosen preset (or custom from/to `YYYY-MM-DD` inputs) into a UTC
 * ISO range. Returns null when a custom range is incomplete or invalid (the
 * caller then holds off fetching). Running-window presets (today / last7 /
 * thisMonth) end at `now`; fixed presets (yesterday) end at that day's end.
 */
export function resolveRange(
  preset: RangePreset,
  customFrom: string,
  customTo: string,
  now: Date = new Date(),
): IsoRange | null {
  switch (preset) {
    case 'today':
      return { from: startOfDay(now).toISOString(), to: now.toISOString() };
    case 'yesterday': {
      const y = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
      return { from: startOfDay(y).toISOString(), to: endOfDay(y).toISOString() };
    }
    case 'last7': {
      // Inclusive of today → 7 calendar days back to and including now.
      const s = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6);
      return { from: startOfDay(s).toISOString(), to: now.toISOString() };
    }
    case 'thisMonth': {
      const s = new Date(now.getFullYear(), now.getMonth(), 1);
      return { from: startOfDay(s).toISOString(), to: now.toISOString() };
    }
    case 'custom': {
      if (!customFrom || !customTo) return null;
      const f = parseLocalYmd(customFrom);
      const t = parseLocalYmd(customTo);
      if (!f || !t) return null;
      if (startOfDay(f).getTime() > endOfDay(t).getTime()) return null;
      return { from: startOfDay(f).toISOString(), to: endOfDay(t).toISOString() };
    }
    default:
      return null;
  }
}

/** Human label for the resolved window, for the page caption. */
export function formatRangeLabel(preset: RangePreset, range: IsoRange | null): string {
  const presetLabel = RANGE_PRESETS.find((p) => p.value === preset)?.label ?? '';
  if (!range) return presetLabel;
  const fmt = (iso: string) =>
    new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  const fromDay = fmt(range.from);
  const toDay = fmt(range.to);
  return fromDay === toDay ? fromDay : `${fromDay} – ${toDay}`;
}
